import React, { memo, useCallback, useState, useEffect } from 'react';
import BaseModal from '@/components/ui/base-modal';
import { DateRange } from 'react-day-picker';
import { Button } from '@/components/_shadcn-ui/button';
import { Check, RotateCcw, CalendarIcon } from 'lucide-react';
import { Calendar as CalendarComponent } from '@/components/_shadcn-ui/calendar';
import { format, setHours, setMinutes, startOfDay } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/_shadcn-ui/popover';
import { cn } from '@/utils/cn';

export interface TimeFrameModalProps {
  isOpen: boolean;
  onClose?: () => void;
  onClear?: () => void;
  onApply?: (dateRange: DateRange) => void;
  initialDateRange?: DateRange;
  submitText?: string;
  title?: string;
  className?: string;
}

interface TempTimeframe {
  startDate: Date | null;
  endDate: Date | null;
  startTime: string;
  endTime: string;
}

const DEFAULT_TEMP_TIMEFRAME: TempTimeframe = {
  startDate: new Date(),
  endDate: setMinutes(setHours(new Date(), 23), 59),
  startTime: format(new Date(), 'HH:mm'),
  endTime: '23:59',
};

const TimeFrameModal = memo<TimeFrameModalProps>(
  ({
    isOpen,
    onClose,
    onClear,
    onApply,
    initialDateRange,
    title = 'Filter by Time Frame',
    submitText = 'Apply Filters',
    className = 'w-fit p-[24px]',
  }) => {
    // HOOKS
    const [tempTimeframe, setTempTimeframe] = useState<TempTimeframe>(() => {
      return {
        startDate: initialDateRange?.from || null,
        endDate: initialDateRange?.to || null,
        startTime: initialDateRange?.from ? format(initialDateRange.from, 'HH:mm') : '00:00',
        endTime: initialDateRange?.to ? format(initialDateRange.to, 'HH:mm') : '23:59',
      };
    });
    const [startMonth, setStartMonth] = useState(() => new Date());
    const [endMonth, setEndMonth] = useState(() => new Date());
    // const [showCloseError, setShowCloseError] = useState(false);

    // HELPERS
    const hasChanges = tempTimeframe.startDate !== null || tempTimeframe.endDate !== null;
    const isDateRangeComplete = tempTimeframe.startDate !== null && tempTimeframe.endDate !== null;
    // const hasValidationErrors =
    //   (tempTimeframe.startDate && tempTimeframe.endDate && tempTimeframe.startDate > tempTimeframe.endDate) ||
    //   (tempTimeframe.startDate && tempTimeframe.endDate && tempTimeframe.endDate < tempTimeframe.startDate);

    // Date validation functions
    const isStartDateDisabled = useCallback(
      (date: Date) => {
        if (!tempTimeframe.endDate) return false;
        return date > tempTimeframe.endDate;
      },
      [tempTimeframe.endDate]
    );

    const isEndDateDisabled = useCallback(
      (date: Date) => {
        if (!tempTimeframe.startDate) return false;
        return date < tempTimeframe.startDate;
      },
      [tempTimeframe.startDate]
    );

    useEffect(() => {
      if (initialDateRange) {
        setTempTimeframe({
          startDate: initialDateRange?.from || null,
          endDate: initialDateRange?.to || null,
          startTime: initialDateRange?.from ? format(initialDateRange.from, 'HH:mm') : '00:00',
          endTime: initialDateRange?.to ? format(initialDateRange.to, 'HH:mm') : '23:59',
        });
      }
    }, [initialDateRange]);

    // HANDLERS
    const handleStartDateSelect = useCallback(
      (date: Date | undefined) => {
        if (!date) {
          setTempTimeframe((prev) => ({
            startDate: null,
            endDate: prev.endDate,
            startTime: prev.startTime,
            endTime: prev.endTime,
          }));
          return;
        }

        // Apply start time to the selected date
        const [hours, minutes] = tempTimeframe.startTime.split(':').map(Number);
        // convert date to hours and minutes with date-fns
        const dateWithTime = setMinutes(setHours(date, hours), minutes);

        setTempTimeframe((prev) => ({
          startDate: dateWithTime,
          endDate: prev.endDate,
          startTime: prev.startTime,
          endTime: prev.endTime,
        }));
      },
      [tempTimeframe.startTime]
    );

    const handleEndDateSelect = useCallback(
      (date: Date | undefined) => {
        if (!date) {
          setTempTimeframe((prev) => ({
            startDate: prev.startDate,
            endDate: null,
            startTime: prev.startTime,
            endTime: prev.endTime,
          }));
          return;
        }

        // Apply end time to the selected date
        const [hours, minutes] = tempTimeframe.endTime.split(':').map(Number);
        const dateWithTime = setMinutes(setHours(date, hours), minutes);

        setTempTimeframe((prev) => ({
          startDate: prev.startDate,
          endDate: dateWithTime,
          startTime: prev.startTime,
          endTime: prev.endTime,
        }));
      },
      [tempTimeframe.endTime]
    );

    const handleTimeChange = useCallback((field: 'startTime' | 'endTime', time: string) => {
      setTempTimeframe((prev) => {
        const newState: TempTimeframe = {
          startDate: prev.startDate,
          endDate: prev.endDate,
          startTime: prev.startTime,
          endTime: prev.endTime,
        };

        newState[field] = time;

        // If there's an existing date, reapply the new time to it
        if (field === 'startTime' && prev.startDate) {
          const [hours, minutes] = time.split(':').map(Number);
          // convert date to hours and minutes with date-fns
          const dateWithNewTime = setMinutes(setHours(prev.startDate, hours), minutes);
          newState.startDate = dateWithNewTime;
        } else if (field === 'endTime' && prev.endDate) {
          const [hours, minutes] = time.split(':').map(Number);
          // convert date to hours and minutes with date-fns
          const dateWithNewTime = setMinutes(setHours(prev.endDate, hours), minutes);
          newState.endDate = dateWithNewTime;
        }

        return newState;
      });
    }, []);

    const handleSubmitFilter = useCallback(() => {
      // Convert to DateRange format
      const dateRange: DateRange = {
        from: tempTimeframe.startDate || undefined,
        to: tempTimeframe.endDate || undefined,
      };

      onClose?.();

      setTimeout(() => {
        onApply?.(dateRange);
      }, 300);
    }, [onApply, onClose, tempTimeframe]);

    const handleClearAll = useCallback(() => {
      setTempTimeframe(DEFAULT_TEMP_TIMEFRAME);
      // setShowCloseError(false);
      onClear?.();
    }, [onClear]);

    const handleClose = useCallback(() => {
      onClose?.();
    }, [onClose]);

    if (!isOpen) return null;

    return (
      <BaseModal
        isButtonClose
        isCloseOutside={false}
        closeButtonClassName="top-[16px] right-[16px]"
        className={cn(
          className,
          // Responsive: full width on mobile, fit on larger screens
          'w-[92vw] max-w-[92vw] sm:w-fit sm:max-w-none p-[16px] sm:p-[20px]'
        )}
        isOpen={isOpen}
        title={title}
        onClose={handleClose}
      >
        {/* {showCloseError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">
              Both start date and end date must be selected before closing the modal.
            </p>
          </div>
        )} */}
        <div className="py-[16px] sm:py-[24px] flex flex-col sm:flex-row gap-[16px] sm:gap-[24px] max-h-[80vh] overflow-y-auto">
          <div className="w-full space-y-4 px-1">
            <div>
              <label
                className={`block text-sm font-medium mb-1 ${
                  tempTimeframe.startDate ? 'text-gray-600' : 'text-gray-600'
                }`}
              >
                Start Date
              </label>
              {tempTimeframe.startDate && tempTimeframe.endDate && tempTimeframe.startDate > tempTimeframe.endDate && (
                <p className="text-xs text-red-600">Start date cannot be after end date</p>
              )}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    data-empty={!tempTimeframe.startDate}
                    className={`data-[empty=true]:text-muted-foreground w-full sm:w-[280px] justify-start text-left font-normal ${
                      tempTimeframe.startDate &&
                      tempTimeframe.endDate &&
                      tempTimeframe.startDate > tempTimeframe.endDate
                        ? 'border-red-500 text-red-600'
                        : tempTimeframe.startDate
                          ? 'border-gray-300 text-gray-600'
                          : ''
                    }`}
                  >
                    <CalendarIcon />
                    {tempTimeframe.startDate ? (
                      format(tempTimeframe.startDate, 'LLL dd, y HH:mm')
                    ) : (
                      <span>Pick a date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <CalendarComponent
                    mode="single"
                    selected={tempTimeframe.startDate ? startOfDay(tempTimeframe.startDate) : undefined}
                    onSelect={handleStartDateSelect}
                    month={startMonth}
                    onMonthChange={setStartMonth}
                    className="rounded-md border-0"
                    disabled={isStartDateDisabled}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="w-full sm:w-fit">
              <label className="block text-sm font-medium text-gray-600 mb-1">Start Time</label>
              <div className="flex gap-2">
                <select
                  value={tempTimeframe.startTime.split(':')[0]}
                  onChange={(e) => {
                    const newTime = `${e.target.value}:${tempTimeframe.startTime.split(':')[1]}`;
                    handleTimeChange('startTime', newTime);
                  }}
                  className="flex-1 min-w-0 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  {Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')).map((hour) => (
                    <option key={hour} value={hour}>
                      {hour}
                    </option>
                  ))}
                </select>
                <span className="text-gray-500 flex items-center">:</span>
                <select
                  value={tempTimeframe.startTime.split(':')[1]}
                  onChange={(e) => {
                    const newTime = `${tempTimeframe.startTime.split(':')[0]}:${e.target.value}`;
                    handleTimeChange('startTime', newTime);
                  }}
                  className="flex-1 min-w-0 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  {Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0')).map((minute) => (
                    <option key={minute} value={minute}>
                      {minute}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="w-full space-y-4 px-1 border-t border-gray-200 mt-4 pt-4 sm:mt-0 sm:pt-0 sm:border-t-0 sm:border-l sm:border-gray-200 sm:pl-4">
            <div>
              <label
                className={`block text-sm font-medium mb-1 ${
                  tempTimeframe.endDate ? 'text-gray-600' : 'text-gray-600'
                }`}
              >
                End Date
              </label>
              {tempTimeframe.startDate && tempTimeframe.endDate && tempTimeframe.endDate < tempTimeframe.startDate && (
                <p className="text-xs text-red-600">End date cannot be before start date</p>
              )}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    data-empty={!tempTimeframe.endDate}
                    className={`data-[empty=true]:text-muted-foreground w-full sm:w-[280px] justify-start text-left font-normal ${
                      tempTimeframe.startDate &&
                      tempTimeframe.endDate &&
                      tempTimeframe.endDate < tempTimeframe.startDate
                        ? 'border-red-500 text-red-600'
                        : tempTimeframe.endDate
                          ? 'border-gray-300 text-gray-600'
                          : ''
                    }`}
                  >
                    <CalendarIcon />
                    {tempTimeframe.endDate ? (
                      format(tempTimeframe.endDate, 'LLL dd, y HH:mm')
                    ) : (
                      <span>Pick a date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <CalendarComponent
                    mode="single"
                    selected={tempTimeframe.endDate ? startOfDay(tempTimeframe.endDate) : undefined}
                    onSelect={handleEndDateSelect}
                    month={endMonth}
                    onMonthChange={setEndMonth}
                    className="rounded-md border-0"
                    disabled={isEndDateDisabled}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="w-full sm:w-fit">
              <label className="block text-sm font-medium text-gray-600 mb-1">End Time</label>
              <div className="flex gap-2">
                <select
                  value={tempTimeframe.endTime.split(':')[0]}
                  onChange={(e) => {
                    const newTime = `${e.target.value}:${tempTimeframe.endTime.split(':')[1]}`;
                    handleTimeChange('endTime', newTime);
                  }}
                  className="flex-1 min-w-0 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  {Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')).map((hour) => (
                    <option key={hour} value={hour}>
                      {hour}
                    </option>
                  ))}
                </select>
                <span className="text-gray-500 flex items-center">:</span>
                <select
                  value={tempTimeframe.endTime.split(':')[1]}
                  onChange={(e) => {
                    const newTime = `${tempTimeframe.endTime.split(':')[0]}:${e.target.value}`;
                    handleTimeChange('endTime', newTime);
                  }}
                  className="flex-1 min-w-0 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  {Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0')).map((minute) => (
                    <option key={minute} value={minute}>
                      {minute}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 py-4 border-gray-200 rounded-b-lg">
          <Button
            onClick={handleClearAll}
            variant="outline"
            disabled={!hasChanges}
            className="w-full sm:w-auto text-gray-600 border-gray-300 hover:bg-gray-100"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Clear All
          </Button>

          <div className="flex flex-col sm:flex-row w-full sm:w-auto items-stretch sm:items-center gap-[12px]">
            <Button
              onClick={handleClose}
              variant="outline"
              className="w-full sm:w-auto text-gray-600 border-gray-300 hover:bg-gray-100"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitFilter}
              disabled={
                !isDateRangeComplete ||
                Boolean(
                  tempTimeframe.startDate && tempTimeframe.endDate && tempTimeframe.endDate < tempTimeframe.startDate
                )
              }
              className={`w-full sm:w-auto min-w-[100px] ${
                !isDateRangeComplete ||
                Boolean(
                  tempTimeframe.startDate && tempTimeframe.endDate && tempTimeframe.endDate < tempTimeframe.startDate
                )
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-[#0C8DBF] text-white hover:bg-[#0C8DBF]/90'
              }`}
            >
              <Check className="w-4 h-4 mr-1" />
              {submitText}
            </Button>
          </div>
        </div>
      </BaseModal>
    );
  }
);

TimeFrameModal.displayName = 'TimeFrameModal';

export default TimeFrameModal;
